import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';
import { fmtAbv } from '../utils/format.js';
import { uiConfirm, uiPrompt } from '../utils/dialogs.js';
import ProductRange from './ProductRange.jsx';

// ── UI primitives ─────────────────────────────────────────────────────────────
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = {
    borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer',
    border: 'none', transition: 'opacity .15s',
    fontSize: size === 'sm' ? 12 : 14,
    padding: size === 'sm' ? '5px 12px' : '9px 18px',
    opacity: props.disabled ? 0.5 : 1,
  };
  const variants = {
    primary: { background: 'var(--pld-green)', color: '#fff' },
    danger:  { background: '#dc2626', color: '#fff' },
    ghost:   { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' },
  };
  return <button {...props} style={{ ...base, ...variants[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 520, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-muted)', lineHeight: 1 }}>x</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)' }}>{children}</select>;
}

function Textarea({ ...props }) {
  return <textarea {...props} style={{ padding: '9px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical', ...props.style }} />;
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t} onClick={() => onChange(t)} style={{
          padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 14, fontWeight: active === t ? 700 : 400,
          color: active === t ? 'var(--pld-green)' : 'var(--text-muted)',
          borderBottom: active === t ? '2px solid var(--pld-green)' : '2px solid transparent',
          marginBottom: -1, transition: 'color .15s',
        }}>{t}</button>
      ))}
    </div>
  );
}

function SectionHeader({ label, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>{label}</h3>
      {action}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft:    { bg: '#f3f4f6', color: '#6b7280', label: 'Draft' },
    active:   { bg: '#dcfce7', color: '#166534', label: 'Active' },
    archived: { bg: '#fef3c7', color: '#92400e', label: 'Archived' },
  };
  const s = map[status] || map.draft;
  return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
}

const SPIRIT_TYPES = ['gin', 'whisky', 'vodka', 'rum', 'brandy', 'liqueur', 'eau_de_vie', 'absinthe', 'neutral_spirit', 'other'];

const LIST_TYPE_LABELS = {
  retail:           'Retail',
  wholesale:        'Wholesale',
  wholesale_bonded: 'Wholesale Bonded',
  on_trade:         'On-trade',
  off_trade:        'Off-trade',
};

const LIST_TYPE_COLOURS = {
  retail:           { bg: '#eff6ff', color: '#1d4ed8' },
  wholesale:        { bg: '#f0fdf4', color: '#166534' },
  wholesale_bonded: { bg: '#faf5ff', color: '#7e22ce' },
  on_trade:         { bg: '#fff7ed', color: '#c2410c' },
  off_trade:        { bg: '#fef9c3', color: '#854d0e' },
};

function ListTypeBadge({ type }) {
  if (!type) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  const c = LIST_TYPE_COLOURS[type] || { bg: '#f3f4f6', color: '#6b7280' };
  return (
    <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: c.bg, color: c.color }}>
      {LIST_TYPE_LABELS[type] || type}
    </span>
  );
}

// ── Brand management ──────────────────────────────────────────────────────────
function BrandFormModal({ brand, onClose, onSaved }) {
  const [form, setForm] = useState({ name: brand?.name ?? '', description: brand?.description ?? '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (brand) {
        await apiFetch(`/clients/brands/${brand.id}`, { method: 'PATCH', body: form });
      } else {
        await apiFetch('/clients/brands', { method: 'POST', body: form });
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={brand ? 'Edit Brand' : 'New Brand'} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Brand name *"><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></Field>
        <Field label="Description"><Textarea rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function BrandsTab() {
  const { data, loading, refetch } = useApi('/clients/brands', []);
  const brands = Array.isArray(data) ? data : [];
  const [modal, setModal] = useState(null);

  return (
    <div>
      <SectionHeader label={`Brands (${brands.length})`} action={<Btn size="sm" onClick={() => setModal({})}>+ New Brand</Btn>} />
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
      ) : brands.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No brands yet.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border)' }}>
              {['Name', 'Description', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {brands.map(b => (
              <tr key={b.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600 }}>{b.name}</td>
                <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{b.description || '-'}</td>
                <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                  <Btn size="sm" variant="ghost" onClick={() => setModal({ brand: b })}>Edit</Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && <BrandFormModal brand={modal.brand} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
    </div>
  );
}

// ── Recipe management ─────────────────────────────────────────────────────────
function RecipeFormModal({ recipe, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:        recipe?.name ?? '',
    spirit_type: recipe?.spirit_type ?? '',
    notes:       recipe?.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (recipe) {
        await apiFetch(`/recipes/${recipe.id}`, { method: 'PUT', body: form });
      } else {
        await apiFetch('/recipes', { method: 'POST', body: { ...form, owner_type: 'PLD' } });
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={recipe ? 'Edit Recipe' : 'New Recipe'} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Recipe name *">
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. PLD Original Gin" required />
        </Field>
        <Field label="Spirit type *">
          <SelectEl value={form.spirit_type} onChange={e => setForm(f => ({ ...f, spirit_type: e.target.value }))} required>
            <option value="">Select...</option>
            {SPIRIT_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
          </SelectEl>
        </Field>
        <Field label="Notes">
          <Textarea rows={4} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Method, process notes, tasting targets..." />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AddIngredientModal({ recipeId, onClose, onSaved }) {
  const { data: stockData } = useApi('/stock');
  const stockItems = Array.isArray(stockData) ? stockData : (stockData?.stock_items ?? []);
  const [form, setForm] = useState({ stock_item_id: '', quantity: '', quantity_unit: '', input_type: 'non_alcoholic', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const upd = k => v => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/recipes/${recipeId}/ingredients`, { method: 'POST', body: { ...form, quantity: parseFloat(form.quantity) } });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  const selected = stockItems.find(s => s.id === form.stock_item_id);

  return (
    <Modal title="Add Ingredient" onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Stock item *">
          <SelectEl value={form.stock_item_id} onChange={e => {
            const item = stockItems.find(s => s.id === e.target.value);
            setForm(f => ({ ...f, stock_item_id: e.target.value, quantity_unit: item?.unit || f.quantity_unit, input_type: item?.is_alcoholic ? 'alcoholic' : 'non_alcoholic' }));
          }} required>
            <option value="">Select item...</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} {s.sku ? `(${s.sku})` : ''}</option>)}
          </SelectEl>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Quantity *"><Input type="number" min="0" step="0.0001" value={form.quantity} onChange={e => upd('quantity')(e.target.value)} required /></Field>
          <Field label="Unit"><Input value={form.quantity_unit} onChange={e => upd('quantity_unit')(e.target.value)} placeholder={selected?.unit || 'litres'} /></Field>
        </div>
        <Field label="Type">
          <SelectEl value={form.input_type} onChange={e => upd('input_type')(e.target.value)}>
            <option value="alcoholic">Alcoholic</option>
            <option value="non_alcoholic">Non-alcoholic</option>
          </SelectEl>
        </Field>
        <Field label="Notes"><Input value={form.notes} onChange={e => upd('notes')(e.target.value)} placeholder="Optional process note for this ingredient" /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Adding...' : 'Add'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function RecipeDetail({ recipeId, onBack }) {
  const { data, loading, refetch } = useApi(`/recipes/${recipeId}`, [recipeId]);
  const recipe = data?.recipe ?? data;
  const { data: ingData, refetch: refetchIng } = useApi(`/recipes/${recipeId}/ingredients`, [recipeId]);
  const ingredients = ingData?.ingredients ?? [];
  const [editing, setEditing] = useState(false);
  const [addIng, setAddIng] = useState(false);
  const [saving, setSaving] = useState(false);

  async function setStatus(status) {
    setSaving(true);
    try { await apiFetch(`/recipes/${recipeId}/status`, { method: 'PATCH', body: { status } }); refetch(); }
    catch (err) { alert(err.message); } finally { setSaving(false); }
  }

  async function removeIngredient(id) {
    if (!(await uiConfirm('Remove this ingredient?'))) return;
    try { await apiFetch(`/recipes/${recipeId}/ingredients/${id}`, { method: 'DELETE' }); refetchIng(); }
    catch (err) { alert(err.message); }
  }

  if (loading || !recipe) return <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 20 }}>Loading...</p>;

  return (
    <div>
      {editing && <RecipeFormModal recipe={recipe} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refetch(); }} />}
      {addIng && <AddIngredientModal recipeId={recipeId} onClose={() => setAddIng(false)} onSaved={() => { setAddIng(false); refetchIng(); }} />}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{recipe.name}</h2>
            <StatusBadge status={recipe.status} />
          </div>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {recipe.spirit_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} &middot; v{recipe.version}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Btn>
          {recipe.status === 'draft'  && <Btn size="sm" onClick={() => setStatus('active')} disabled={saving}>Activate</Btn>}
          {recipe.status === 'active' && <Btn size="sm" variant="ghost" onClick={() => setStatus('archived')} disabled={saving}>Archive</Btn>}
        </div>
      </div>

      {recipe.notes && (
        <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
          {recipe.notes}
        </div>
      )}

      <SectionHeader label={`Ingredients (${ingredients.length})`} action={<Btn size="sm" onClick={() => setAddIng(true)}>+ Add Ingredient</Btn>} />
      {ingredients.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>No ingredients added yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['#', 'Ingredient', 'Quantity', 'Type', 'Notes', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ing, i) => (
                <tr key={ing.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)', width: 32 }}>{i + 1}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{ing.item_name || ing.stock_item_id}</td>
                  <td style={{ padding: '10px 12px' }}>{ing.quantity} {ing.quantity_unit || ''}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{ing.input_type === 'alcoholic' ? 'Alcoholic' : 'Non-alcoholic'}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{ing.notes || '-'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <Btn size="sm" variant="ghost" onClick={() => removeIngredient(ing.id)}>Remove</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecipesTab() {
  const { data, loading, refetch } = useApi('/recipes?owner_type=PLD', []);
  const recipes = Array.isArray(data) ? data : (data?.recipes ?? []);
  const [modal, setModal] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  if (selectedId) return <RecipeDetail recipeId={selectedId} onBack={() => setSelectedId(null)} />;

  const filtered = statusFilter === 'all' ? recipes : recipes.filter(r => r.status === statusFilter);

  return (
    <div>
      <SectionHeader
        label={`Recipes (${filtered.length})`}
        action={<Btn size="sm" onClick={() => setModal({})}>+ New Recipe</Btn>}
      />
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {['all', 'draft', 'active', 'archived'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', border: 'none',
            background: statusFilter === s ? 'var(--pld-green)' : 'var(--surface-2)',
            color: statusFilter === s ? '#fff' : 'var(--text-muted)',
          }}>{s.charAt(0).toUpperCase() + s.slice(1)}</button>
        ))}
      </div>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>
          {statusFilter === 'active' ? 'No active recipes. Create one or activate a draft.' : 'No recipes found.'}
        </p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Name', 'Spirit type', 'Version', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelectedId(r.id)}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{r.spirit_type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>v{r.version}</td>
                  <td style={{ padding: '10px 12px' }}><StatusBadge status={r.status} /></td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>View &rsaquo;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <RecipeFormModal recipe={modal.recipe} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
    </div>
  );
}

// ── Batches ───────────────────────────────────────────────────────────────────
// Stage 1 of the batches/traceability/recalls build: batch CRUD + allocation
// lifecycle (allocate → dispatch → cancel). Traceability tests (Salsa page)
// and the recall workflow are separate follow-up stages that build on this.

function fmtDateGB(d) {
  return d ? new Date(d).toLocaleDateString('en-GB') : '-';
}

function BatchFormModal({ batch, onClose, onSaved }) {
  const isEdit = !!batch;
  const { data: brandsData } = useApi('/clients/brands', []);
  const brands = Array.isArray(brandsData) ? brandsData : [];
  const { data: stockData } = useApi('/stock', []);
  const stockItems = (Array.isArray(stockData) ? stockData : (stockData?.stock_items ?? []))
    .filter(s => s.category === 'bottle_finished');

  const [form, setForm] = useState({
    brand_id:            batch?.brand_id ?? '',
    product_name:        batch?.product_name ?? '',
    bottle_size_cl:      batch?.bottle_size_cl ?? '',
    abv:                 batch?.abv ?? '',
    total_units_bottled: batch?.total_units_bottled ?? '',
    units_per_case:      batch?.units_per_case ?? 6,
    lpa:                 batch?.lpa ?? '',
    label_lot_ref:       batch?.label_lot_ref ?? '',
    bottling_date:       batch?.bottling_date ? batch.bottling_date.slice(0, 10) : '',
    stock_item_id:       batch?.stock_item_id ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (isEdit) {
        // updateBatch only persists a limited field set server-side.
        await apiFetch(`/own-brand/batches/${batch.id}`, {
          method: 'PATCH',
          body: {
            product_name:   form.product_name,
            label_lot_ref:  form.label_lot_ref || null,
            bottling_date:  form.bottling_date || null,
            lpa:            form.lpa || null,
            stock_item_id:  form.stock_item_id || null,
          },
        });
      } else {
        await apiFetch('/own-brand/batches', {
          method: 'POST',
          body: {
            ...form,
            bottle_size_cl:      parseInt(form.bottle_size_cl, 10),
            abv:                 parseFloat(form.abv),
            total_units_bottled: parseInt(form.total_units_bottled, 10),
            units_per_case:      parseInt(form.units_per_case, 10) || 6,
            lpa:                 form.lpa ? parseFloat(form.lpa) : null,
            bottling_date:       form.bottling_date || null,
            stock_item_id:       form.stock_item_id || null,
          },
        });
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={isEdit ? 'Edit Batch' : 'New Batch'} onClose={onClose} width={560}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}

        {!isEdit && (
          <>
            <Field label="Brand *">
              <SelectEl value={form.brand_id} onChange={e => set('brand_id', e.target.value)} required>
                <option value="">Select brand...</option>
                {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </SelectEl>
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Bottle size (cl) *"><Input type="number" min="1" value={form.bottle_size_cl} onChange={e => set('bottle_size_cl', e.target.value)} required /></Field>
              <Field label="ABV (%) *"><Input type="number" min="0" step="0.01" value={form.abv} onChange={e => set('abv', e.target.value)} required /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Total units bottled *"><Input type="number" min="1" value={form.total_units_bottled} onChange={e => set('total_units_bottled', e.target.value)} required /></Field>
              <Field label="Units per case"><Input type="number" min="1" value={form.units_per_case} onChange={e => set('units_per_case', e.target.value)} /></Field>
            </div>
          </>
        )}

        <Field label="Product name *"><Input value={form.product_name} onChange={e => set('product_name', e.target.value)} placeholder="e.g. PLD Original Gin 70cl" required /></Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="LPA (litres)"><Input type="number" min="0" step="0.0001" value={form.lpa} onChange={e => set('lpa', e.target.value)} /></Field>
          <Field label="Bottling date"><Input type="date" value={form.bottling_date} onChange={e => set('bottling_date', e.target.value)} /></Field>
        </div>

        <Field label="Label lot ref"><Input value={form.label_lot_ref} onChange={e => set('label_lot_ref', e.target.value)} placeholder="For traceability / label batch code" /></Field>

        <Field label="Finished stock item" hint="The bottled SKU this batch draws stock from (needed for dispatch to record a stock movement).">
          <SelectEl value={form.stock_item_id} onChange={e => set('stock_item_id', e.target.value)}>
            <option value="">— none linked —</option>
            {stockItems.map(s => <option key={s.id} value={s.id}>{s.name} {s.sku ? `(${s.sku})` : ''}</option>)}
          </SelectEl>
        </Field>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AllocateModal({ batchId, onClose, onSaved }) {
  const { data: outletData } = useApi('/own-brand/outlets?limit=all', []);
  const outlets = Array.isArray(outletData) ? outletData : [];
  const [form, setForm] = useState({ outlet_id: '', units_allocated: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/own-brand/batches/${batchId}/allocations`, {
        method: 'POST',
        body: { ...form, units_allocated: parseInt(form.units_allocated, 10) },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Allocate to Outlet" onClose={onClose} width={460}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Outlet *">
          <SelectEl value={form.outlet_id} onChange={e => setForm(f => ({ ...f, outlet_id: e.target.value }))} required>
            <option value="">Select outlet...</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </SelectEl>
        </Field>
        <Field label="Units *"><Input type="number" min="1" value={form.units_allocated} onChange={e => setForm(f => ({ ...f, units_allocated: e.target.value }))} required /></Field>
        <Field label="Notes"><Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Allocating...' : 'Allocate'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

const ALLOC_STATUS_COLOURS = {
  pending:    { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
  dispatched: { bg: '#dbeafe', color: '#1d4ed8', label: 'Dispatched' },
  delivered:  { bg: '#dcfce7', color: '#166534', label: 'Delivered' },
  cancelled:  { bg: '#f3f4f6', color: '#6b7280', label: 'Cancelled' },
};
function AllocationStatusBadge({ status }) {
  const s = ALLOC_STATUS_COLOURS[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>;
}

function BatchDetail({ batchId, onBack }) {
  const { data: batch, loading, refetch } = useApi(`/own-brand/batches/${batchId}`, [batchId]);
  const { data: allocData, refetch: refetchAlloc } = useApi(`/own-brand/batches/${batchId}/allocations`, [batchId]);
  const allocations = Array.isArray(allocData) ? allocData : [];
  const [editing, setEditing] = useState(false);
  const [allocating, setAllocating] = useState(false);
  const [busy, setBusy] = useState(null);

  async function dispatchAllocation(allocationId) {
    if (!(await uiConfirm('Mark this allocation as dispatched? This records a stock movement and cannot be undone from here.'))) return;
    setBusy(allocationId);
    try {
      await apiFetch(`/own-brand/batches/${batchId}/allocations/${allocationId}/dispatch`, { method: 'POST', body: {} });
      refetch(); refetchAlloc();
    } catch (err) { alert(err.message); } finally { setBusy(null); }
  }

  async function cancelAllocation(allocationId) {
    if (!(await uiConfirm('Cancel this pending allocation? Units return to available.'))) return;
    setBusy(allocationId);
    try {
      await apiFetch(`/own-brand/batches/${batchId}/allocations/${allocationId}/cancel`, { method: 'POST', body: {} });
      refetch(); refetchAlloc();
    } catch (err) { alert(err.message); } finally { setBusy(null); }
  }

  if (loading || !batch) return <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 20 }}>Loading...</p>;

  const stats = [
    { label: 'Available', value: batch.units_available, colour: '#166534' },
    { label: 'Reserved',  value: batch.units_reserved,   colour: '#854d0e' },
    { label: 'Allocated', value: batch.units_allocated,  colour: '#1d4ed8' },
    { label: 'Dispatched', value: batch.units_dispatched, colour: '#6b7280' },
    { label: 'Recalled',  value: batch.units_recalled,   colour: '#dc2626' },
  ];

  return (
    <div>
      {editing && <BatchFormModal batch={batch} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refetch(); }} />}
      {allocating && <AllocateModal batchId={batchId} onClose={() => setAllocating(false)} onSaved={() => { setAllocating(false); refetch(); refetchAlloc(); }} />}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 2 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>{batch.batch_ref}</span>
            {batch.recall_initiated_at && (
              <span style={{ padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>RECALLED</span>
            )}
          </div>
          <h2 style={{ margin: '4px 0 0', fontSize: 18, fontWeight: 700 }}>{batch.product_name}</h2>
          <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {batch.brand_name} · {batch.bottle_size_cl}cl · {fmtAbv(batch.abv)}% ABV
            {batch.bottling_date ? ` · Bottled ${fmtDateGB(batch.bottling_date)}` : ''}
            {batch.label_lot_ref ? ` · Lot ${batch.label_lot_ref}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Btn>
          {!batch.recall_initiated_at && batch.units_available > 0 && (
            <Btn size="sm" onClick={() => setAllocating(true)}>+ Allocate</Btn>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 24 }}>
        {stats.map(s => (
          <div key={s.label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.colour }}>{s.value}</div>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <SectionHeader label={`Allocations (${allocations.length})`} />
      {allocations.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '24px 0' }}>No allocations yet.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Outlet', 'Units', 'Status', 'Dispatch Date', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allocations.map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{a.allocation_ref}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{a.outlet_name}</td>
                  <td style={{ padding: '10px 12px' }}>{a.units_allocated}</td>
                  <td style={{ padding: '10px 12px' }}><AllocationStatusBadge status={a.status} /></td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{fmtDateGB(a.dispatch_date)}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    {a.status === 'pending' && (
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Btn size="sm" variant="ghost" disabled={busy === a.id} onClick={() => cancelAllocation(a.id)}>Cancel</Btn>
                        <Btn size="sm" disabled={busy === a.id} onClick={() => dispatchAllocation(a.id)}>{busy === a.id ? 'Dispatching...' : 'Dispatch'}</Btn>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Recalls ───────────────────────────────────────────────────────────────────
// Stage 3 of the batches/traceability/recalls build: the two-person-approval
// recall workflow, sitting under a batch (stage 1) and complementing the
// traceability tests on the Salsa page (stage 2) — a real recall is exactly
// the scenario those tests rehearse.

const RECALL_STATUS_COLOURS = {
  initiated:          { bg: '#fef9c3', color: '#854d0e', label: 'Awaiting Approval' },
  notifications_sent: { bg: '#fee2e2', color: '#991b1b', label: 'Active — Notified' },
  closed:             { bg: '#dcfce7', color: '#166534', label: 'Closed' },
};
function RecallStatusBadge({ status }) {
  const s = RECALL_STATUS_COLOURS[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return <span style={{ padding: '3px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{s.label}</span>;
}

const NOTIF_STATUS_COLOURS = {
  not_recalled:      { bg: '#f3f4f6', color: '#6b7280', label: 'Not recalled' },
  notified:          { bg: '#fef9c3', color: '#854d0e', label: 'Notified' },
  response_received: { bg: '#dbeafe', color: '#1d4ed8', label: 'Response received' },
  returned:          { bg: '#dcfce7', color: '#166534', label: 'Returned' },
  destroyed:         { bg: '#ede9fe', color: '#5b21b6', label: 'Destroyed' },
  not_returnable:    { bg: '#fee2e2', color: '#991b1b', label: 'Not returnable' },
};
function NotifStatusBadge({ status }) {
  const s = NOTIF_STATUS_COLOURS[status] || { bg: '#f3f4f6', color: '#6b7280', label: status };
  return <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
}

function InitiateRecallModal({ batchId, onClose, onSaved }) {
  const [form, setForm] = useState({ recall_type: 'voluntary', scope: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/own-brand/batches/${batchId}/recalls`, { method: 'POST', body: form });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Initiate Recall" onClose={onClose} width={520}>
      <form onSubmit={submit}>
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--radius)', padding: '10px 14px', fontSize: 12, color: '#991b1b', marginBottom: 16 }}>
          This requires two-person approval: a different user must approve before any outlet notifications go out.
        </div>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Type">
          <SelectEl value={form.recall_type} onChange={e => set('recall_type', e.target.value)}>
            <option value="voluntary">Voluntary</option>
            <option value="mandatory">Mandatory</option>
          </SelectEl>
        </Field>
        <Field label="Scope *"><Textarea rows={2} value={form.scope} onChange={e => set('scope', e.target.value)} placeholder="e.g. All units of this batch dispatched to wholesale outlets since 1 June" required /></Field>
        <Field label="Reason *"><Textarea rows={3} value={form.reason} onChange={e => set('reason', e.target.value)} placeholder="What triggered this recall" required /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" type="submit" disabled={saving}>{saving ? 'Submitting...' : 'Initiate Recall'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function UpdateNotificationModal({ recallId, notification, onClose, onSaved }) {
  const [form, setForm] = useState({
    units_returned:        notification.units_returned ?? 0,
    response_received_at:  notification.response_received_at ? notification.response_received_at.slice(0, 10) : '',
    follow_up_required:    notification.follow_up_required ?? false,
    follow_up_due_at:      notification.follow_up_due_at ? notification.follow_up_due_at.slice(0, 10) : '',
    notes:                 notification.notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/own-brand/recalls/${recallId}/notifications/${notification.id}`, {
        method: 'PATCH',
        body: {
          units_returned:       parseInt(form.units_returned, 10) || 0,
          response_received_at: form.response_received_at || null,
          follow_up_required:   form.follow_up_required,
          follow_up_due_at:     form.follow_up_required ? (form.follow_up_due_at || null) : null,
          notes:                form.notes || null,
        },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={`Update — ${notification.outlet_name}`} onClose={onClose} width={480}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Units returned"><Input type="number" min="0" max={notification.units_to_return} value={form.units_returned} onChange={e => set('units_returned', e.target.value)} /></Field>
          <Field label="Response received"><Input type="date" value={form.response_received_at} onChange={e => set('response_received_at', e.target.value)} /></Field>
        </div>
        <Field label="">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={form.follow_up_required} onChange={e => set('follow_up_required', e.target.checked)} />
            Follow-up required
          </label>
        </Field>
        {form.follow_up_required && (
          <Field label="Follow-up due"><Input type="date" value={form.follow_up_due_at} onChange={e => set('follow_up_due_at', e.target.value)} /></Field>
        )}
        <Field label="Notes"><Input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional" /></Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function RecallDetail({ recallId, onBack, onChanged }) {
  const { data: recall, loading, refetch } = useApi(`/own-brand/recalls/${recallId}`, [recallId]);
  const [busy, setBusy] = useState(false);
  const [updatingNotif, setUpdatingNotif] = useState(null);

  async function approve() {
    if (!(await uiConfirm('Approve this recall? This fires notifications to every outlet that received dispatched stock from this batch.'))) return;
    setBusy(true);
    try { await apiFetch(`/own-brand/recalls/${recallId}/approve`, { method: 'POST' }); refetch(); onChanged(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  async function markFsa() {
    const fsa_ref = (await uiPrompt('FSA notification reference (leave blank if not yet available):', '')) ?? '';
    setBusy(true);
    try { await apiFetch(`/own-brand/recalls/${recallId}/notify-fsa`, { method: 'POST', body: { fsa_ref: fsa_ref || null } }); refetch(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  async function markHmrc() {
    if (!(await uiConfirm('Confirm HMRC has been notified of this recall?'))) return;
    setBusy(true);
    try { await apiFetch(`/own-brand/recalls/${recallId}/notify-hmrc`, { method: 'POST' }); refetch(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  async function close() {
    if (!(await uiConfirm('Close this recall? Only do this once all outlets have responded and product has been returned or accounted for.'))) return;
    setBusy(true);
    try { await apiFetch(`/own-brand/recalls/${recallId}/close`, { method: 'POST' }); refetch(); onChanged(); }
    catch (err) { alert(err.message); } finally { setBusy(false); }
  }

  if (loading || !recall) return <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 20 }}>Loading...</p>;

  return (
    <div>
      {updatingNotif && (
        <UpdateNotificationModal
          recallId={recallId}
          notification={updatingNotif}
          onClose={() => setUpdatingNotif(null)}
          onSaved={() => { setUpdatingNotif(null); refetch(); }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, padding: 0, marginTop: 2 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{recall.recall_ref}</span>
            <RecallStatusBadge status={recall.status} />
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: recall.recall_type === 'mandatory' ? '#dc2626' : 'var(--text-muted)' }}>{recall.recall_type}</span>
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{recall.scope}</p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {recall.status === 'initiated' && <Btn size="sm" variant="danger" disabled={busy} onClick={approve}>Approve Recall</Btn>}
          {recall.status === 'notifications_sent' && !recall.reported_to_fsa && <Btn size="sm" variant="ghost" disabled={busy} onClick={markFsa}>Mark FSA Notified</Btn>}
          {recall.status === 'notifications_sent' && !recall.reported_to_hmrc && <Btn size="sm" variant="ghost" disabled={busy} onClick={markHmrc}>Mark HMRC Notified</Btn>}
          {recall.status === 'notifications_sent' && <Btn size="sm" disabled={busy} onClick={close}>Close Recall</Btn>}
        </div>
      </div>

      <div style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '12px 16px', fontSize: 13, marginBottom: 20 }}>
        <strong>Reason:</strong> {recall.reason}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Outlets Affected', value: recall.total_outlets_affected ?? 0 },
          { label: 'Units Affected',   value: recall.total_units_affected ?? 0 },
          { label: 'Units Returned',   value: recall.total_units_returned ?? 0 },
          { label: 'Reported', value: `FSA ${recall.reported_to_fsa ? '✓' : '—'} · HMRC ${recall.reported_to_hmrc ? '✓' : '—'}` },
        ].map(s => (
          <div key={s.label} style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '10px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{s.value}</div>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      <SectionHeader label={`Outlet Notifications (${(recall.notifications || []).length})`} />
      {!(recall.notifications || []).length ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Notifications are created once the recall is approved.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Outlet', 'Contact', 'To Return', 'Returned', 'Status', 'Follow-up', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recall.notifications.map(n => (
                <tr key={n.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{n.outlet_name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{n.contact_name_used || n.contact_email_used || '-'}</td>
                  <td style={{ padding: '10px 12px' }}>{n.units_to_return}</td>
                  <td style={{ padding: '10px 12px' }}>{n.units_returned}</td>
                  <td style={{ padding: '10px 12px' }}><NotifStatusBadge status={n.recall_status ?? (n.units_returned >= n.units_to_return ? 'returned' : 'notified')} /></td>
                  <td style={{ padding: '10px 12px', color: n.follow_up_required ? '#dc2626' : 'var(--text-muted)' }}>{n.follow_up_required ? `Due ${fmtDateGB(n.follow_up_due_at)}` : '-'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <Btn size="sm" variant="ghost" onClick={() => setUpdatingNotif(n)}>Update</Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function RecallsSection({ batchId, onChanged }) {
  const { data, loading, refetch } = useApi(`/own-brand/batches/${batchId}/recalls`, [batchId]);
  const recalls = Array.isArray(data) ? data : [];
  const [initiating, setInitiating] = useState(false);
  const [selectedRecallId, setSelectedRecallId] = useState(null);

  function changed() { refetch(); onChanged?.(); }

  if (selectedRecallId) {
    return <RecallDetail recallId={selectedRecallId} onBack={() => { setSelectedRecallId(null); changed(); }} onChanged={changed} />;
  }

  return (
    <div>
      {initiating && <InitiateRecallModal batchId={batchId} onClose={() => setInitiating(false)} onSaved={() => { setInitiating(false); changed(); }} />}
      <SectionHeader label={`Recalls (${recalls.length})`} action={<Btn size="sm" variant="danger" onClick={() => setInitiating(true)}>Initiate Recall</Btn>} />
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
      ) : recalls.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '16px 0' }}>No recalls on this batch.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Ref', 'Type', 'Status', 'Outlets', 'Units Affected', 'Units Returned', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recalls.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelectedRecallId(r.id)}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{r.recall_ref}</td>
                  <td style={{ padding: '10px 12px' }}>{r.recall_type}</td>
                  <td style={{ padding: '10px 12px' }}><RecallStatusBadge status={r.status} /></td>
                  <td style={{ padding: '10px 12px' }}>{r.total_outlets_affected ?? '-'}</td>
                  <td style={{ padding: '10px 12px' }}>{r.total_units_affected ?? '-'}</td>
                  <td style={{ padding: '10px 12px' }}>{r.total_units_returned ?? '-'}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>View &rsaquo;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 28, paddingTop: 24, borderTop: '1px solid var(--border)' }}>
        <RecallsSection batchId={batchId} onChanged={refetch} />
      </div>
    </div>
  );
}

function BatchesTab() {
  const { data, loading, refetch } = useApi('/own-brand/batches', []);
  const batches = Array.isArray(data) ? data : (data?.batches ?? []);
  const [modal, setModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  if (selectedId) return <BatchDetail batchId={selectedId} onBack={() => setSelectedId(null)} />;

  return (
    <div>
      <SectionHeader label={`Batches (${batches.length})`} action={<Btn size="sm" onClick={() => setModal(true)}>+ New Batch</Btn>} />
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
      ) : batches.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No batches yet. Create one after a bottling run.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Batch Ref', 'Product', 'Brand', 'Bottled', 'Available', 'Reserved', 'Allocated', 'Dispatched', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {batches.map(b => (
                <tr key={b.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelectedId(b.id)}>
                  <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                    {b.batch_ref}
                    {b.recall_initiated_at && <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 99, fontSize: 10, fontWeight: 700, background: '#fee2e2', color: '#dc2626' }}>RECALLED</span>}
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 600 }}>{b.product_name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{b.brand_name}</td>
                  <td style={{ padding: '10px 12px' }}>{b.total_units_bottled}</td>
                  <td style={{ padding: '10px 12px', color: '#166534', fontWeight: 600 }}>{b.units_available}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{b.units_reserved}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{b.units_allocated}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{b.units_dispatched}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>View &rsaquo;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <BatchFormModal onClose={() => setModal(false)} onSaved={() => { setModal(false); refetch(); }} />}
    </div>
  );
}

// ── Price Lists ───────────────────────────────────────────────────────────────

function PriceListFormModal({ priceList, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:           priceList?.name           ?? '',
    list_type:      priceList?.list_type      ?? '',
    currency:       priceList?.currency       ?? 'GBP',
    effective_from: priceList?.effective_from ? priceList.effective_from.slice(0, 10) : '',
    notes:          priceList?.notes          ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      const body = { ...form, effective_from: form.effective_from || null };
      if (priceList) {
        await apiFetch(`/price-lists/${priceList.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/price-lists', { method: 'POST', body });
      }
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={priceList ? 'Edit Price List' : 'New Price List'} onClose={onClose}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        <Field label="Name *">
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Trade Price List 2026" required />
        </Field>
        <Field label="List type (optional)">
          <SelectEl value={form.list_type || ''} onChange={e => setForm(f => ({ ...f, list_type: e.target.value }))}>
            <option value="">—</option>
            {Object.entries(LIST_TYPE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </SelectEl>
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Currency">
            <SelectEl value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
              <option value="GBP">GBP</option>
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
            </SelectEl>
          </Field>
          <Field label="Effective from">
            <Input type="date" value={form.effective_from} onChange={e => setForm(f => ({ ...f, effective_from: e.target.value }))} />
          </Field>
        </div>
        <Field label="Notes">
          <Textarea rows={3} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional notes about this price list" />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function AddPriceLineModal({ priceListId, existingItemIds, onClose, onSaved }) {
  const { data: productsData } = useApi('/price-lists/products');
  const products = productsData?.products ?? [];
  const available = products.filter(p => !existingItemIds.includes(p.id));
  const [form, setForm] = useState({ stock_item_id: '', unit_price: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await apiFetch(`/price-lists/${priceListId}/lines`, {
        method: 'POST',
        body: { ...form, unit_price: parseFloat(form.unit_price) },
      });
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title="Add Product" onClose={onClose} width={460}>
      <form onSubmit={submit}>
        {error && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{error}</p>}
        {available.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>All bottled products are already on this list. Add more bottle-finished stock items first.</p>
        ) : (
          <>
            <Field label="Product *">
              <SelectEl value={form.stock_item_id} onChange={e => setForm(f => ({ ...f, stock_item_id: e.target.value }))} required>
                <option value="">Select product...</option>
                {available.map(p => (
                  <option key={p.id} value={p.id}>{p.name} {p.sku ? `(${p.sku})` : ''}{p.abv ? ` - ${fmtAbv(p.abv)}%` : ''}</option>
                ))}
              </SelectEl>
            </Field>
            <Field label="Unit price (ex VAT) *">
              <Input
                type="number" min="0" step="0.0001" required
                value={form.unit_price}
                onChange={e => setForm(f => ({ ...f, unit_price: e.target.value }))}
                placeholder="e.g. 18.50"
              />
            </Field>
            <Field label="Notes">
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional" />
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
              <Btn type="submit" disabled={saving || !form.stock_item_id}>{saving ? 'Adding...' : 'Add Product'}</Btn>
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}

function EditPriceModal({ line, onClose, onSaved }) {
  const [price, setPrice] = useState(String(line.unit_price));
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try {
      await apiFetch(`/price-lists/${line.price_list_id}/lines/${line.id}`, {
        method: 'PUT', body: { unit_price: parseFloat(price) },
      });
      onSaved();
    } catch (err) { alert(err.message); } finally { setSaving(false); }
  }

  return (
    <Modal title={`Edit price — ${line.item_name}`} onClose={onClose} width={380}>
      <form onSubmit={submit}>
        <Field label="Unit price (ex VAT) *">
          <Input type="number" min="0" step="0.0001" value={price} onChange={e => setPrice(e.target.value)} required autoFocus />
        </Field>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" type="button" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function PriceListDetail({ listId, onBack }) {
  const { data, loading, refetch } = useApi(`/price-lists/${listId}`, [listId]);
  const pl    = data?.price_list ?? null;
  const lines = data?.lines ?? [];

  const [editing,     setEditing]     = useState(false);
  const [addLine,     setAddLine]     = useState(false);
  const [editLine,    setEditLine]    = useState(null);
  const [saving,      setSaving]      = useState(false);

  async function setStatus(status) {
    setSaving(true);
    try { await apiFetch(`/price-lists/${listId}/status`, { method: 'PATCH', body: { status } }); refetch(); }
    catch (err) { alert(err.message); } finally { setSaving(false); }
  }

  async function removeLine(lineId) {
    if (!(await uiConfirm('Remove this product from the list?'))) return;
    try { await apiFetch(`/price-lists/${listId}/lines/${lineId}`, { method: 'DELETE' }); refetch(); }
    catch (err) { alert(err.message); }
  }

  if (loading || !pl) return <p style={{ color: 'var(--text-muted)', fontSize: 14, padding: 20 }}>Loading...</p>;

  const existingItemIds = lines.map(l => l.stock_item_id);
  const fmt = v => new Intl.NumberFormat('en-GB', { style: 'currency', currency: pl.currency || 'GBP' }).format(v);

  return (
    <div>
      {editing  && <PriceListFormModal priceList={pl} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refetch(); }} />}
      {addLine  && <AddPriceLineModal priceListId={listId} existingItemIds={existingItemIds} onClose={() => setAddLine(false)} onSaved={() => { setAddLine(false); refetch(); }} />}
      {editLine && <EditPriceModal line={editLine} onClose={() => setEditLine(null)} onSaved={() => { setEditLine(null); refetch(); }} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 24 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 20, lineHeight: 1, padding: 0, marginTop: 2 }}>&larr;</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{pl.name}</h2>
            <ListTypeBadge type={pl.list_type} />
            <StatusBadge status={pl.status} />
          </div>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {pl.currency}
            {pl.effective_from ? ` · Effective ${new Date(pl.effective_from).toLocaleDateString('en-GB')}` : ''}
            {pl.notes ? ` · ${pl.notes}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <Btn size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Btn>
          {pl.status === 'draft'  && <Btn size="sm" onClick={() => setStatus('active')} disabled={saving}>Activate</Btn>}
          {pl.status === 'active' && <Btn size="sm" variant="ghost" onClick={() => setStatus('archived')} disabled={saving}>Archive</Btn>}
        </div>
      </div>

      {/* Lines table */}
      <SectionHeader
        label={`Products (${lines.length})`}
        action={<Btn size="sm" onClick={() => setAddLine(true)}>+ Add Product</Btn>}
      />
      {lines.length === 0 ? (
        <div style={{ padding: '32px 24px', background: 'var(--surface-2)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>
          No products yet. Add your first product above.
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Product', 'SKU', 'ABV', 'Unit price (ex VAT)', 'Notes', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map(line => (
                <tr key={line.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{line.item_name}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontFamily: 'monospace', fontSize: 12 }}>{line.sku || '-'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{line.abv != null ? `${fmtAbv(line.abv)}%` : '-'}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 700, fontFamily: 'monospace', fontSize: 14 }}>{fmt(line.unit_price)}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{line.notes || '-'}</td>
                  <td style={{ padding: '11px 14px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <Btn size="sm" variant="ghost" onClick={() => setEditLine({ ...line, price_list_id: listId })}>Edit price</Btn>
                      <Btn size="sm" variant="danger" onClick={() => removeLine(line.id)}>Remove</Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Import from Brewman: its "Price Lists" export (the lists) or a list's
// "List Prices" export (that list's prices, replacing what's there), or its
// outlet "Pricing" export (outlet discounts, replacing the Brewman ones).
function discountText(d) {
  const v = (x) => (d.unit === 'percent' ? `${Number(x)}%` : `£${Math.abs(Number(x)).toFixed(2)}`);
  const one = (x) => (Number(x) < 0 ? `${v(x)} added` : `${v(x)} off`);
  const parts = [];
  if (Number(d.flat)) parts.push(`${one(d.flat)}${d.unit === 'percent' ? '' : ' each'}`);
  for (const t of d.tiers || []) parts.push(`${one(t.discount)} from ${t.qty}${d.tier_basis === 'category' ? ' (order total)' : ''}`);
  return parts.join(', ') || 'none';
}

function BrewmanImportModal({ lists, onClose, onDone }) {
  const [csv, setCsv] = useState(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [target, setTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  async function read(file) {
    setErr(''); setPreview(null); setResult(null);
    if (!file) return;
    const text = await file.text();
    setCsv(text); setFileName(file.name);
    try {
      const p = await apiFetch('/price-lists/import/preview', { method: 'POST', body: { csv: text } });
      setPreview(p);
      // A guess from the file name ("Trade prices.csv" → Trade).
      const hit = lists.find(l => file.name.toLowerCase().includes(l.name.toLowerCase()));
      if (hit) setTarget(hit.id);
    } catch (e) { setErr(e.message); }
  }
  async function go() {
    setBusy(true); setErr('');
    try {
      const body = { csv };
      if (preview.kind === 'prices') {
        if (!target) throw new Error('Choose which price list these prices are for');
        body.price_list_id = target;
      }
      setResult(await apiFetch('/price-lists/import', { method: 'POST', body }));
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }
  const sub = { fontSize: 12, color: 'var(--text-muted)' };
  const listOf = (rows, fmt) => rows.length ? <div style={{ ...sub, maxHeight: 120, overflow: 'auto', marginTop: 4 }}>{rows.map((r, i) => <div key={i}>{fmt(r)}</div>)}</div> : null;
  return (
    <Modal title="Import from Brewman" onClose={onClose}>
      {err && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 12px' }}>{err}</p>}
      {result ? (
        <div>
          {'created' in result
            ? <p style={{ fontSize: 14 }}>✓ {result.created} price list{result.created === 1 ? '' : 's'} added, {result.updated} updated.</p>
            : result.kind === 'discounts'
              ? <p style={{ fontSize: 14 }}>✓ {result.imported} outlet discount{result.imported === 1 ? '' : 's'} imported.{result.lists_set?.length ? ` ${result.lists_set.length} outlet${result.lists_set.length === 1 ? '' : 's'} had no price list and now ${result.lists_set.length === 1 ? 'has' : 'have'} the one ${result.lists_set.length === 1 ? 'its' : 'their'} discounts are on.` : ''}</p>
              : <p style={{ fontSize: 14 }}>✓ {result.imported} price{result.imported === 1 ? '' : 's'} imported into <strong>{result.name}</strong>.</p>}
          {result.unknown_outlets?.length > 0 && (<>
            <p style={{ fontSize: 13, color: '#b45309', margin: '10px 0 0' }}>{result.unknown_outlets.length} outlet{result.unknown_outlets.length === 1 ? ' isn\'t' : 's aren\'t'} in Blake under that name — their discounts were left out:</p>
            {listOf(result.unknown_outlets, o => o)}
          </>)}
          {result.unknown_products?.length > 0 && (<>
            <p style={{ fontSize: 13, color: '#b45309', margin: '10px 0 0' }}>{result.unknown_products.length} discount{result.unknown_products.length === 1 ? ' is' : 's are'} on a product Blake doesn't have under that name — left out:</p>
            {listOf(result.unknown_products, u => `${u.outlet} — ${u.product}`)}
          </>)}
          {result.unknown?.length > 0 && (<>
            <p style={{ fontSize: 13, color: '#b45309', margin: '10px 0 0' }}>{result.unknown.length} product{result.unknown.length === 1 ? '' : 's'} with a price aren't in Blake under that Brewman code — put the code on the stock item as its legacy SKU and import again:</p>
            {listOf(result.unknown, u => `${u.code} — ${u.name} (£${Number(u.price).toFixed(2)})`)}
          </>)}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}><Btn onClick={onClose}>Done</Btn></div>
        </div>
      ) : (<>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 12px' }}>
          Export from Brewman <strong>Price Lists</strong> (the list names and settings), a list's <strong>List Prices</strong> (its prices), or outlet <strong>Pricing</strong> (outlet discounts). Prices match products on their Brewman product code — the stock item's legacy SKU; discounts match outlets and products by name.
        </p>
        <Field label="Brewman export (.csv)"><input type="file" accept=".csv,text/csv" onChange={e => read(e.target.files?.[0])} /></Field>
        {preview?.kind === 'lists' && (
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <strong>{preview.lists.length} price lists</strong> in {fileName}:
            {listOf(preview.lists, l => `${l.name}${l.exists ? ' (already in Blake — settings updated)' : ''}${l.use_gross_price ? ' · prices include VAT' : ''}${l.allow_discounts ? '' : ' · no discounts'}`)}
          </div>
        )}
        {preview?.kind === 'prices' && (
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <Field label="These are the prices for">
              <SelectEl value={target} onChange={e => setTarget(e.target.value)}>
                <option value="">Choose the price list…</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name}{l.line_count ? ` (${l.line_count} prices now — replaced)` : ''}</option>)}
              </SelectEl>
            </Field>
            <div><strong>{preview.lines.length}</strong> product{preview.lines.length === 1 ? '' : 's'} will come in.</div>
            {preview.unknown.length > 0 && <div style={{ color: '#b45309' }}>{preview.unknown.length} priced product{preview.unknown.length === 1 ? ' isn\'t' : 's aren\'t'} in Blake under that code (listed after importing).</div>}
            <div style={sub}>Left out: {preview.not_available.length} not available on this list in Brewman, {preview.no_price.length} with no price.</div>
          </div>
        )}
        {preview?.kind === 'discounts' && (
          <div style={{ fontSize: 13, marginBottom: 12 }}>
            <div><strong>{preview.discounts.length}</strong> outlet discount{preview.discounts.length === 1 ? '' : 's'} will come in, replacing those imported before.</div>
            {listOf(preview.discounts, d => `${d.outlet} — ${d.item_name}: ${discountText(d)}`)}
            {preview.lists_to_set.length > 0 && <div style={{ ...sub, marginTop: 6 }}>{preview.lists_to_set.length} outlet{preview.lists_to_set.length === 1 ? ' has' : 's have'} no price list yet and will get the one {preview.lists_to_set.length === 1 ? 'its' : 'their'} discounts are on.</div>}
            {(preview.unknown_outlets.length > 0 || preview.unknown_products.length > 0) && <div style={{ color: '#b45309', marginTop: 6 }}>{preview.unknown_outlets.length} outlet{preview.unknown_outlets.length === 1 ? '' : 's'} and {preview.unknown_products.length} product discount{preview.unknown_products.length === 1 ? '' : 's'} don't match Blake by name (listed after importing).</div>}
            {preview.zero.length > 0 && <div style={sub}>Left out: {preview.zero.length} with no discount (0).</div>}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn disabled={!preview || busy || (preview.kind === 'prices' && (!target || !preview.lines.length)) || (preview.kind === 'discounts' && !preview.discounts.length)} onClick={go}>{busy ? 'Importing…' : 'Import'}</Btn>
        </div>
      </>)}
    </Modal>
  );
}

function PriceListsTab() {
  const { data, loading, refetch } = useApi('/price-lists');
  const lists = data?.price_lists ?? [];
  const [modal, setModal] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [importing, setImporting] = useState(false);

  if (selectedId) return <PriceListDetail listId={selectedId} onBack={() => setSelectedId(null)} />;

  const filtered = typeFilter === 'all' ? lists : lists.filter(l => l.list_type === typeFilter);

  return (
    <div>
      <SectionHeader
        label={`Price Lists (${filtered.length})`}
        action={<div style={{ display: 'flex', gap: 8 }}><Btn size="sm" variant="ghost" onClick={() => setImporting(true)}>Import from Brewman</Btn><Btn size="sm" onClick={() => setModal({})}>+ New Price List</Btn></div>}
      />
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setTypeFilter('all')} style={{
          padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
          background: typeFilter === 'all' ? 'var(--pld-green)' : 'var(--surface-2)',
          color: typeFilter === 'all' ? '#fff' : 'var(--text-muted)',
        }}>All</button>
        {Object.entries(LIST_TYPE_LABELS).filter(([k]) => lists.some(l => l.list_type === k)).map(([k, v]) => (
          <button key={k} onClick={() => setTypeFilter(k)} style={{
            padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none',
            background: typeFilter === k ? 'var(--pld-green)' : 'var(--surface-2)',
            color: typeFilter === k ? '#fff' : 'var(--text-muted)',
          }}>{v}</button>
        ))}
      </div>
      {loading ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</p>
      ) : filtered.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>No price lists yet. Create your first one above.</p>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--surface-2)' }}>
                {['Name', 'Type', 'Products', 'Settings', 'Effective from', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '9px 14px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(pl => (
                <tr key={pl.id} style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => setSelectedId(pl.id)}>
                  <td style={{ padding: '11px 14px', fontWeight: 600 }}>{pl.name}</td>
                  <td style={{ padding: '11px 14px' }}><ListTypeBadge type={pl.list_type} /></td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>{pl.line_count}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)', fontSize: 12 }}>{[pl.currency !== 'GBP' ? pl.currency : null, pl.use_gross_price ? 'inc VAT' : null, pl.allow_discounts === false ? 'no discounts' : null, pl.source === 'brewman' ? 'from Brewman' : null].filter(Boolean).join(' · ') || '—'}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-muted)' }}>
                    {pl.effective_from ? new Date(pl.effective_from).toLocaleDateString('en-GB') : '-'}
                  </td>
                  <td style={{ padding: '11px 14px' }}><StatusBadge status={pl.status} /></td>
                  <td style={{ padding: '11px 14px', textAlign: 'right', color: 'var(--text-muted)', fontSize: 12 }}>View &rsaquo;</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && <PriceListFormModal priceList={modal.priceList} onClose={() => setModal(null)} onSaved={() => { setModal(null); refetch(); }} />}
      {importing && <BrewmanImportModal lists={lists} onClose={() => setImporting(false)} onDone={refetch} />}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function OwnBrand() {
  const [tab, setTab] = useState('Recipes');

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Own Brand</h1>
        <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>Manage your product recipes, price lists and brand portfolio</p>
      </div>
      <TabBar tabs={['Recipes', 'Batches', 'Products we sell', 'Price Lists', 'Brands']} active={tab} onChange={setTab} />
      {tab === 'Recipes'     && <RecipesTab />}
      {tab === 'Batches'     && <BatchesTab />}
      {tab === 'Products we sell' && <ProductRange />}
      {tab === 'Price Lists' && <PriceListsTab />}
      {tab === 'Brands'      && <BrandsTab />}
    </div>
  );
}
